/**
 * 지식 베이스 컨텍스트
 * 지식 상태 관리 및 비즈니스 로직
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  createKnowledge,
  getKnowledge,
  getKnowledgeList,
  updateKnowledge,
  deleteKnowledge,
  toggleKnowledgeFavorite,
  toggleKnowledgeActive,
  searchKnowledge,
  KNOWLEDGE_CATEGORIES
} from '../services/knowledgeService';

const KnowledgeContext = createContext();

export function useKnowledge() {
  const context = useContext(KnowledgeContext);
  if (!context) {
    throw new Error('useKnowledge must be used within KnowledgeProvider');
  }
  return context;
}

export function KnowledgeProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    searchTerm: '',
    showInactive: false,
    sortBy: 'updatedAt',
    sortOrder: 'desc'
  });

  // 지식 목록 로드
  const loadItems = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const filterOptions = {
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      if (filters.category) {
        filterOptions.category = filters.category;
      }

      if (!filters.showInactive) {
        filterOptions.isActive = true;
      }

      const fetchedItems = await getKnowledgeList(user.uid, filterOptions);
      setItems(fetchedItems);
    } catch (err) {
      console.error('Error loading knowledge:', err);
      setError('지식을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  // 초기 로드
  useEffect(() => {
    if (user) {
      loadItems();
    }
  }, [user, loadItems]);

  // 지식 생성
  const createItem = async (data) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      const newItem = await createKnowledge(user.uid, data);
      setItems(prev => [newItem, ...prev]);
      return newItem;
    } catch (err) {
      console.error('Error creating knowledge:', err);
      setError('지식 생성에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 지식 조회
  const getItem = async (itemId) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      const item = await getKnowledge(itemId, user.uid);
      return item;
    } catch (err) {
      console.error('Error getting knowledge:', err);
      setError('지식을 불러오는데 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 지식 업데이트
  const updateItem = async (itemId, updates) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      const updatedItem = await updateKnowledge(itemId, user.uid, updates);
      setItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, ...updatedItem } : item
      ));
      return updatedItem;
    } catch (err) {
      console.error('Error updating knowledge:', err);
      setError('지식 업데이트에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 지식 삭제
  const deleteItem = async (itemId) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      await deleteKnowledge(itemId, user.uid);
      setItems(prev => prev.filter(item => item.id !== itemId));
      return true;
    } catch (err) {
      console.error('Error deleting knowledge:', err);
      setError('지식 삭제에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 즐겨찾기 토글
  const toggleFavorite = async (itemId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const isFavorite = await toggleKnowledgeFavorite(itemId, user.uid);
      setItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, isFavorite } : item
      ));
      return isFavorite;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError('즐겨찾기 설정에 실패했습니다.');
      throw err;
    }
  };

  // 활성화 토글
  const toggleActive = async (itemId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const isActive = await toggleKnowledgeActive(itemId, user.uid);
      setItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, isActive } : item
      ));
      return isActive;
    } catch (err) {
      console.error('Error toggling active:', err);
      setError('활성화 설정에 실패했습니다.');
      throw err;
    }
  };

  // 검색
  const search = async (searchTerm) => {
    if (!user) return [];

    setLoading(true);
    setError(null);

    try {
      const results = await searchKnowledge(user.uid, searchTerm);
      return results;
    } catch (err) {
      console.error('Error searching knowledge:', err);
      setError('검색에 실패했습니다.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // 필터링된 항목
  const filteredItems = items.filter(item => {
    // 검색어 필터
    if (filters.searchTerm) {
      const lowerSearch = filters.searchTerm.toLowerCase();
      const titleMatch = item.title?.toLowerCase().includes(lowerSearch);
      const contentMatch = item.content?.toLowerCase().includes(lowerSearch);
      const tagMatch = item.tags?.some(tag =>
        tag.toLowerCase().includes(lowerSearch)
      );
      if (!titleMatch && !contentMatch && !tagMatch) return false;
    }

    return true;
  });

  // 카테고리별 그룹화
  const groupedItems = filteredItems.reduce((acc, item) => {
    const category = item.category || 'general_notes';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  // 통계
  const stats = {
    total: items.length,
    active: items.filter(i => i.isActive !== false).length,
    favorites: items.filter(i => i.isFavorite).length,
    byCategory: Object.keys(KNOWLEDGE_CATEGORIES).reduce((acc, cat) => {
      acc[cat] = items.filter(i => i.category === cat).length;
      return acc;
    }, {})
  };

  // 필터 업데이트
  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // 필터 초기화
  const resetFilters = () => {
    setFilters({
      category: '',
      searchTerm: '',
      showInactive: false,
      sortBy: 'updatedAt',
      sortOrder: 'desc'
    });
  };

  const value = {
    items,
    filteredItems,
    groupedItems,
    loading,
    error,
    filters,
    stats,
    categories: KNOWLEDGE_CATEGORIES,
    createItem,
    getItem,
    updateItem,
    deleteItem,
    toggleFavorite,
    toggleActive,
    search,
    updateFilters,
    resetFilters,
    loadItems
  };

  return (
    <KnowledgeContext.Provider value={value}>
      {children}
    </KnowledgeContext.Provider>
  );
}
